import { State, MainThunkDispatch } from "../../Reducers"
import { failMessage } from "../Client/MessagesActions"
import { APIAuthenticationType } from "../Types"
import { UserMeta, APIUserModel } from "../../Models/API/UserModel"
import { apiFetch } from "../Client/NetworkActions"
import { apiEndpoints } from "../../Routes/api"
import { safePromise } from "../../Helpers/Functions/promise"
import { isAuthenticated } from "../../Helpers/Functions/Selectors/auth"
import { userLogout } from "../Auth/AuthActions"
import { segmentIdentity } from "../../Helpers/Functions/analytics"
import differenceInMinutes from "date-fns/differenceInMinutes"
import { fromUnixTime } from "date-fns"

const ERR_NO_API_KEY_FOUND = "empty"

export const createAPIKey = () => async (dispatch: MainThunkDispatch) => {
    const { error } = await safePromise(
        dispatch(apiFetch("CREATE_API_KEY", apiEndpoints.createAPIKey({})))
    )

    if (error !== undefined) {
        dispatch(failMessage("Could not create API Key", error))
        throw error
    }
}

export const deleteAPIKey =
    (apiKey: string) => async (dispatch: MainThunkDispatch) => {
        const { error } = await safePromise(
            dispatch(
                apiFetch(
                    `DELETE_API_KEY_${apiKey}`,
                    apiEndpoints.deleteAPIKey({ apiKey }),
                    undefined,
                    undefined,
                    true
                )
            )
        )

        if (error !== undefined) {
            dispatch(failMessage("Could not delete API Key", error))
            throw error
        }
    }

/**
 * When a user's auth state changes (ie they are logged in),
 * we then send a request to an endpoint to fetch the api key
 * this will utilise the AWS token to authenticate
 * all requests from the web app or anything can use this token
 * if not logged in, it will set to null
 */
export const getAPIKeys = () => async (dispatch: MainThunkDispatch) => {
    const { data, error } = await safePromise(
        dispatch(apiFetch("GET_API_KEYS", apiEndpoints.getAPIKeys({})))
    )

    if (error !== undefined) throw error
    if (data === undefined) return // throttled in NetworkAction

    dispatch({
        type: APIAuthenticationType.setAPIKeys,
        payload: data
    })
    /**
     * This user doesn't have an API Key, but we
     * are here to use the API, so it's safe to
     * assume that it's appropriate to create at
     * least 1 to get started.
     */
    if (Array.isArray(data) && data.length === 0)
        throw new Error(ERR_NO_API_KEY_FOUND)
}

export const getOrCreateAPIKeys = () => async (dispatch: MainThunkDispatch) => {
    const { error } = await safePromise(dispatch(getAPIKeys()))

    if (error !== undefined) {
        if (error.message !== ERR_NO_API_KEY_FOUND) {
            throw error
        } else {
            // create api key, then get api key
            const { error: error2 } = await safePromise(
                dispatch(createAPIKey())
            )

            if (error2 !== undefined) throw error2

            return dispatch(getAPIKeys())
        }
    }
}

export const getUser = () => async (dispatch: MainThunkDispatch) => {
    const { data, error } = await safePromise<APIUserModel>(
        dispatch(apiFetch("GET_USER", apiEndpoints.userBase({})))
    )

    if (error !== undefined) throw error
    if (data === undefined) return // throttled in NetworkAction

    dispatch({
        type: APIAuthenticationType.setUserInfo,
        payload: data
    })

    return data
}

export const userAPIInit =
    (
        sendAnalyticsEvent: boolean,
        newUser: boolean = false,
        googleOAuthHack: boolean = false
    ) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const [{ error: keysError }, { data: user, error: userError }] =
            await Promise.all([
                // we can make these API calls without having API keys
                safePromise(dispatch(getOrCreateAPIKeys())),
                safePromise(dispatch(getUser()))
            ])

        try {
            if (userError !== undefined) {
                throw userError
            }

            if (keysError !== undefined) {
                throw keysError
            }

            if (user === undefined) {
                throw new Error("User is not found.")
            }

            const created = user.created
                ? parseInt(user.created, 10) * 0.001
                : undefined

            const recentCreated = googleOAuthHack
                ? created !== undefined
                    ? differenceInMinutes(new Date(), fromUnixTime(created)) < 2
                    : false
                : false

            if (sendAnalyticsEvent) {
                segmentIdentity(getState(), newUser || recentCreated)
            }
        } catch (e) {
            // force logout so that user can attempt to sign-in again
            await dispatch(userLogout(true))
            throw e
        }
    }

export const updateUserData =
    (meta: UserMeta) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const state = getState()
        const { user } = state.api.authentication
        if (!isAuthenticated(state) || !user)
            throw Error("Please log in to update user.")

        const options: RequestInit = {
            method: "PUT",
            body: JSON.stringify({
                metadata: {
                    ...user.metadata,
                    ...meta
                }
            })
        }
        const { data, error } = await safePromise(
            dispatch(
                apiFetch("UPDATE_USER", apiEndpoints.userBase({}), options)
            )
        )

        if (error !== undefined) throw error
        if (data === undefined) throw Error("User could not be updated.")

        dispatch({
            type: APIAuthenticationType.setUserInfo,
            payload: data
        })

        return Promise.resolve("User successfully updated.")
    }

export const getOrgMemberOwnerExistsValue =
    (orgUsername: string, memberId: number, code: string) =>
    async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.orgInvitationUserExists({
                orgUsername,
                memberId,
                code
            })
            const data = await dispatch(
                apiFetch<{ exists: boolean; email: string }>(
                    endpoint.url,
                    endpoint
                )
            )

            if (data === undefined) {
                throw Error("Request throttled")
            }

            dispatch({
                type: APIAuthenticationType.setOrgMemberOwnerExists,
                meta: {
                    memberId
                },
                payload: {
                    exists: data.exists,
                    email: data.email
                }
            })
        } catch (error) {
            dispatch(failMessage("Could not get invitation data", error))
        }
        return
    }

export const getWorkerOwnerExistsValue =
    (workerId: number, code: string) => async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.workerInvitationUserExists({
                workerId,
                code
            })
            const data = await dispatch(
                apiFetch<{ exists: boolean; email: string }>(
                    endpoint.url,
                    endpoint
                )
            )

            if (data === undefined) {
                throw Error("Request throttled")
            }

            dispatch({
                type: APIAuthenticationType.setWorkerOwnerExists,
                meta: {
                    workerId
                },
                payload: {
                    exists: data.exists,
                    email: data.email
                }
            })
        } catch (error) {
            dispatch(failMessage("Could not get invitation data", error))
        }
        return
    }
